import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePack, useDeletePack } from '../hooks/usePacks';
import { useTests } from '../hooks/useTests';
import Card from '../components/Card';
import Button from '../components/Button';
import Skeleton from '../components/Skeleton';
import { useRole } from '../hooks/useRole';
import { ChevronLeft, Pencil, Trash2, Package, Calendar, MapPin, User, FlaskConical, Zap, Plus, Factory, Cpu, ChevronRight } from 'lucide-react';

export default function PackDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pack, isLoading: packLoading } = usePack(id || '');
  const { data: testsData, isLoading: testsLoading } = useTests(id || '');
  const deletePack = useDeletePack();
  const { isEditor } = useRole();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deletePack.mutateAsync(id);
      navigate('/packs');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to delete pack');
    }
  };

  if (packLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton variant="circle" className="w-8 h-8" />
          <Skeleton className="h-6 w-32" />
        </div>
        <Card className="!p-3 space-y-4">
          <Skeleton className="h-4 w-24" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </Card>
        <Card className="!p-3">
          <Skeleton className="h-4 w-24 mb-2" />
          <div className="grid grid-cols-3 gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  if (!pack) {
    return (
      <Card>
        <div className="text-center py-12">
          <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <p className="text-lg font-semibold text-gray-700 mb-2">Pack not found</p>
          <p className="text-sm text-gray-500 mb-6">The pack you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/packs')}>
            Back to Packs
          </Button>
        </div>
      </Card>
    );
  }

  const tests = testsData?.items || [];
  const cells = pack.cells_data || [];
  const canDelete = tests.length === 0;

  // Info Item Component
  const InfoItem = ({ icon: Icon, label, value, iconBg = "bg-gray-100", iconColor = "text-gray-600" }: {
    icon: any;
    label: string;
    value: string | number;
    iconBg?: string;
    iconColor?: string;
  }) => (
    <div className="flex items-center gap-2.5 py-2">
      <div className={`w-7 h-7 rounded-md ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );

  // Status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Deployed': return 'bg-green-100 text-green-700';
      case 'Scrapped': return 'bg-red-100 text-red-700';
      case 'Decommissioned': return 'bg-amber-100 text-amber-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with Back Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/packs')}
          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900 truncate">{pack.pack_id}</h1>
          {pack.batch_id && (
            <p className="text-xs text-gray-500">Batch: {pack.batch_id}</p>
          )}
        </div>
      </div>

      {/* Action Buttons - Compact Row */}
      {isEditor && (
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/tests/new', { state: { packId: pack.pack_id } })}
            className="flex items-center gap-2 text-sm py-2"
          >
            <Plus className="w-4 h-4" />
            Add Test
          </Button>
          <button
            onClick={() => navigate(`/packs/${id}/edit`)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
          >
            <Pencil className="w-4 h-4" />
          </button>
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Pack Info Card */}
      <Card className="!p-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pack Information</h3>
        <div className="grid grid-cols-2 gap-x-4">
          {pack.bp_manufacturer && (
            <InfoItem icon={Factory} label="Manufacturer" value={pack.bp_manufacturer} />
          )}
          {pack.customer && (
            <InfoItem icon={User} label="Customer" value={pack.customer} iconBg="bg-blue-50" iconColor="text-blue-600" />
          )}
          {pack.customer_location && (
            <InfoItem icon={MapPin} label="Location" value={pack.customer_location} />
          )}
          {pack.dispatch_dt && (
            <InfoItem icon={Calendar} label="Dispatch" value={new Date(pack.dispatch_dt).toLocaleDateString()} />
          )}
          {pack.bms && (
            <InfoItem icon={Cpu} label="BMS" value={pack.bms} />
          )}
          {pack.status && (
            <div className="flex items-center gap-2.5 py-2">
              <div className={`w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0`}>
                <Package className={`w-3.5 h-3.5 text-gray-600`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">Status</p>
                <div className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(pack.status)}`}>
                  {pack.status}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Configuration Card */}
      <Card className="!p-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Configuration</h3>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="text-lg font-bold text-blue-700">{pack.cells_in_series || 14}</p>
            <p className="text-[10px] text-blue-600 font-medium">Series</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-700">{pack.cells_in_parallel || 1}</p>
            <p className="text-[10px] text-gray-600 font-medium">Parallel</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-lg font-bold text-green-700">{pack.total_cells || cells.length}/14</p>
            <p className="text-[10px] text-green-600 font-medium">Total</p>
          </div>
        </div>
      </Card>

      {/* Cells Card */}
      <Card className="!p-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Cells ({cells.length}/14)
        </h3>
        {cells.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <Zap className="mx-auto h-8 w-8 mb-2" />
            <p className="text-sm">No cells assigned</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {cells.map((cell, index) => (
              <Link
                key={index}
                to={`/cells/${cell.cell_id}`}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
              >
                <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                  {cell.position || index + 1}
                </div>
                <span className="text-xs font-medium text-gray-700 truncate flex-1 group-hover:text-blue-600">
                  {cell.cell_id}
                </span>
                <ChevronRight className="w-3 h-3 text-gray-400" />
              </Link>
            ))}
          </div>
        )}
      </Card>

      {/* Test History Card */}
      <Card className="!p-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Test History
        </h3>
        {testsLoading ? (
          <div className="space-y-2 py-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : tests.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <FlaskConical className="mx-auto h-8 w-8 mb-2" />
            <p className="text-sm">No tests recorded</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tests.map((test) => (
              <div key={test.test_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FlaskConical className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {test.test_date ? new Date(test.test_date).toLocaleDateString() : 'No date'}
                    </span>
                    {test.cycle && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                        {test.cycle}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                    {test.c_capacity && <span>C: {test.c_capacity}</span>}
                    {test.d_capacity && <span>D: {test.d_capacity}</span>}
                    {test.test_operator && <span>{test.test_operator}</span>}
                  </div>
                </div>
                {isEditor && (
                  <button
                    onClick={() => navigate(`/tests/${pack.pack_id}/${test.test_id}/edit`)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    aria-label="Edit test"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">Delete Pack?</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently delete <span className="font-medium text-gray-700">{pack.pack_id}</span> and all associated data.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deletePack.isPending}
                className="flex-1 py-2 px-4 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deletePack.isPending ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletePack.isPending}
                className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




